/**
 * The shared "soft surface" treatment for the four semantic tones — a
 * saturated border, that tone's pale surface as the background, and the
 * saturated colour again as the text (`border-X bg-XSurface text-X`).
 *
 * Before this existed, the exact same three classes, for the exact same
 * three tones, were hand-typed once each in `Badge.tsx`, `StatusChip.tsx`,
 * and `StatusMessage.tsx` — three literal copies, not three considered
 * designs, and `ErrorState.tsx` hand-typed a fourth copy of `danger` split
 * across two elements. Nothing enforced that a fourth copy would still
 * match; this is that enforcement.
 *
 * `danger` is exported here as the soft treatment used by `StatusMessage`
 * and `ErrorState` (an inline banner and a full alert both meant to be read
 * at length). `Badge`'s own `danger` is deliberately NOT this: its badge is
 * a notification *count*, conventionally rendered as a solid, high-urgency
 * fill (`bg-danger text-white`) rather than a soft surface, the way an
 * unread-count badge reads industry-wide — so `Badge.tsx` keeps that one
 * tone as its own local override rather than importing it from here.
 *
 * Exported as three atomic pieces (`TONE_BORDER`/`TONE_SURFACE_BG`/
 * `TONE_TEXT`) rather than only the combined string: every consumer but
 * `ErrorState` applies all three to one element and wants `SOFT_TONE_CLASS`,
 * but `ErrorState` splits border+background onto its container and text
 * colour onto a nested paragraph, and forcing that markup to change just to
 * consume one combined string was not worth the risk to a component this
 * visible for a pure deduplication.
 */
export type SemanticTone = 'danger' | 'info' | 'success' | 'warning';

export const TONE_BORDER: Record<SemanticTone, string> = {
  danger: 'border-danger',
  info: 'border-info',
  success: 'border-success',
  warning: 'border-warning',
};

export const TONE_SURFACE_BG: Record<SemanticTone, string> = {
  danger: 'bg-dangerSurface',
  info: 'bg-infoSurface',
  success: 'bg-successSurface',
  warning: 'bg-warningSurface',
};

export const TONE_TEXT: Record<SemanticTone, string> = {
  danger: 'text-danger',
  info: 'text-info',
  success: 'text-success',
  warning: 'text-warning',
};

const TONES: readonly SemanticTone[] = ['danger', 'info', 'success', 'warning'];

/** `${border} ${surface background} ${text}` for each tone, in that order. */
export const SOFT_TONE_CLASS: Record<SemanticTone, string> = Object.fromEntries(
  TONES.map((tone) => [tone, `${TONE_BORDER[tone]} ${TONE_SURFACE_BG[tone]} ${TONE_TEXT[tone]}`]),
) as Record<SemanticTone, string>;
